import invariant from 'invariant';
import { always, map } from 'ramda';

import { Sound } from '../entities/categoryEntity';

// tslint:disable:object-literal-sort-keys

const windowWidth: BreakpointValues = {
  large: 992,
  medium: 768,
  small: 576,
  xlarge: 1200,
  xsmall: 0,
};

const windowHeight: BreakpointValues = {
  large: 1080,
  // small desktop or tablet
  medium: 900,

  small: 768,

  xlarge: 1440,
  xsmall: 0,
};

export interface BreakpointValues {
  large: number;
  medium: number;
  small: number;
  xlarge: number;
  xsmall: number;
}

const halveValues = value => value / 2;

// we're dynamically creating these so they can be updated in response to window height and widths without doing math
const widgetHeight: BreakpointValues = map(halveValues, windowHeight);

const widgetWidth: BreakpointValues = map(halveValues, windowWidth);

export const breakpoint = {
  widgetHeight,
  widgetWidth,
  windowHeight,
  windowWidth,
};

export interface SoundAlert {
  id: Sound;
  name: string;
}

export const soundAlerts: SoundAlert[] = [
  {
    id: '',
    name: 'None',
  },
  {
    id: 'alarm',
    name: 'Alarm',
  },
  {
    id: 'droplet',
    name: 'Droplet',
  },
  {
    id: 'glass',
    name: 'Glass',
  },
  {
    id: 'morsecode',
    name: 'Morsecode',
  },
  {
    id: 'opencan',
    name: 'Opencan',
  },
  {
    id: 'pot',
    name: 'Pot',
  },
  {
    id: 'shatter',
    name: 'Shatter',
  },
  {
    id: 'siren',
    name: 'Siren',
  },
];

// tslint:enable:object-literal-sort-keys

/*
  Manually triggers a resize event
*/
export function syntheticWindowResize() {
  window.setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new Event('syntheticResize'));
  }, 0);
}

export interface OpenWindowOptions {
  errorMessage?: string;
  free?: boolean;
  onError?(): any;
}

export const openWindow = (
  url?: string,
  target?: string,
  features?: string,
  _replace?: boolean,
  options?: OpenWindowOptions,
) => {
  const { errorMessage = '', free = false, onError = always(undefined) } = options || {};

  try {
    const popout = window.open(url, target, features);
    if (free && popout) {
      popout.opener = null;
    }
    return popout;
  } catch (_error: any) {
    onError();
    if (errorMessage) {
      // TODO use a toast
      window.alert(errorMessage);
    }

    return null;
  }
};

export const assertUnreachable = (value: never) => {
  invariant(false, `an unreachable statement was paradoxically reached: ${JSON.stringify(value)}`);
};

export const getHelpSiteUrl = (medium: string): string =>
  encodeURI(`http://pro.benzinga.help/?utm_campaign=Help Site&utm_source=Benzinga Pro&medium=${medium}`);

export const BZ_CONTACT_PHONE = '1 (877) 440-9464';
export const BZ_CONTACT_PHONE_HREF = 'tel:+18774409464';
export const BZ_CONTACT_EMAIL = 'verify@benzinga.com';
export const NBSP = '\xa0';
export const NO_VALUE = '--';
export const PRODUCT_FEEDBACK = 'https://portal.prodpad.com/25266';

export type AuthKey = string;
export type HexColor = string;
export type ISO8601 = string; // e.g. "2017-11-03T13:17:03Z"

/*
 * UnixTime in Seconds
 */
export type UnixTimestamp = string;
export type JSONString = string;
export type MECSSectorCode = string; // a string that starts with "MECS:" e.g. "MECS:201"
export type Milliseconds = number;
export type Seconds = number;
export type StringNumber = string; // a string that only contains numbers
export type URLString = string;
